<p-toast />

<div class="card">
    <div class="font-semibold text-xl mb-4">Categories</div>

    @if (categoryService.error()) {
    <p-message
        severity="error"
        [text]="categoryService.error()!"
        styleClass="mb-3"
    />
    }

    <p-table
        [value]="categoryService.categories()"
        [loading]="categoryService.loading()"
        dataKey="CategoryId"
        [rowTrackBy]="trackById"
        [rows]="10"
        [paginator]="true"
        [showGridlines]="true"
        responsiveLayout="scroll"
    >
        <ng-template #caption>
            <div class="flex items-center justify-between gap-3 flex-wrap">
                <div class="font-medium">Global game categories</div>
                <div class="flex items-center gap-2">
                    <p-button
                        label="Refresh"
                        icon="pi pi-refresh"
                        severity="secondary"
                        outlined
                        [loading]="categoryService.loading()"
                        (onClick)="refresh()"
                    />
                    <!-- "Add Category" is hidden for now — sub-categories are not planned.
                         The create flow (openCreate/create) stays in the component. -->
                </div>
            </div>
        </ng-template>
        <ng-template #header>
            <tr>
                <th>Name</th>
                <th style="width: 14rem">Type</th>
                <th style="width: 20rem">Category ID</th>
                <th style="width: 8rem">Actions</th>
            </tr>
        </ng-template>
        <ng-template #body let-category>
            <tr>
                <td><span class="font-medium">{{ category.Name }}</span></td>
                <td>{{ typeLabelOf(category.Config.type) }}</td>
                <td>
                    <span class="text-sm font-mono text-muted-color break-all"
                        >{{ category.CategoryId }}</span
                    >
                </td>
                <td>
                    <p-button
                        icon="pi pi-pencil"
                        severity="warn"
                        [rounded]="true"
                        [outlined]="true"
                        pTooltip="Edit"
                        (onClick)="openEdit(category)"
                    />
                </td>
            </tr>
        </ng-template>
        <ng-template #emptymessage>
            <tr>
                <td colspan="4" class="text-center py-8 text-muted-color">
                    No categories yet.
                </td>
            </tr>
        </ng-template>
    </p-table>
</div>

<p-dialog
    [header]="dialogTitle()"
    [visible]="dialogVisible()"
    (visibleChange)="dialogVisible.set($event)"
    [style]="{ width: '64rem' }"
    [modal]="true"
    [closable]="!saving()"
>
    <p-fluid>
        <div class="flex flex-col gap-4">
            <p-tabs
                [value]="dialogTab()"
                (valueChange)="dialogTab.set($event ?? 'general')"
            >
                <p-tablist>
                    <p-tab value="general"
                        ><span class="flex items-center gap-2"
                            ><i class="pi pi-cog"></i> General</span
                        ></p-tab
                    >
                    <p-tab value="currency"
                        ><span class="flex items-center gap-2"
                            ><i class="pi pi-money-bill"></i> Currency</span
                        ></p-tab
                    >
                    <p-tab value="kyc"
                        ><span class="flex items-center gap-2"
                            ><i class="pi pi-verified"></i> KYC</span
                        ></p-tab
                    >
                    @if (type() === CategoryType.SkillBasedGames) {
                    <p-tab value="withdraw"
                        ><span class="flex items-center gap-2"
                            ><i class="pi pi-wallet"></i> Withdraw</span
                        ></p-tab
                    >
                    }
                    <p-tab value="legal"
                        ><span class="flex items-center gap-2"
                            ><i class="pi pi-shield"></i> Legal</span
                        ></p-tab
                    >
                </p-tablist>
                <p-tabpanels>
                    <p-tabpanel value="general">
                        <div class="flex flex-col gap-4">
                            @if (mode() === 'add') {
                            <div class="flex flex-col gap-1">
                                <label for="categoryId" class="font-semibold"
                                    >ID</label
                                >
                                <input
                                    pInputText
                                    id="categoryId"
                                    [ngModel]="id()"
                                    (ngModelChange)="id.set($event)"
                                    placeholder="skill-based-games"
                                    [disabled]="saving()"
                                />
                                @if (idError()) {
                                <small class="text-red-500"
                                    >{{ idError() }}</small
                                >
                                }
                            </div>
                            }

                            <div class="flex flex-col gap-1">
                                <label for="categoryName" class="font-semibold"
                                    >Name</label
                                >
                                <input
                                    pInputText
                                    id="categoryName"
                                    [ngModel]="name()"
                                    (ngModelChange)="name.set($event)"
                                    placeholder="Skill-based Games"
                                    [disabled]="saving()"
                                />
                                @if (nameError()) {
                                <small class="text-red-500"
                                    >{{ nameError() }}</small
                                >
                                }
                            </div>

                            <div class="flex flex-col gap-1">
                                <label for="categoryType" class="font-semibold"
                                    >Type</label
                                >
                                @if (mode() === 'add') {
                                <p-select
                                    inputId="categoryType"
                                    [options]="typeOptions"
                                    optionLabel="label"
                                    optionValue="value"
                                    [ngModel]="type()"
                                    (ngModelChange)="type.set($event)"
                                    appendTo="body"
                                    [disabled]="saving()"
                                />
                                } @else {
                                <input
                                    pInputText
                                    id="categoryType"
                                    [ngModel]="typeLabel()"
                                    [disabled]="true"
                                    readonly
                                />
                                <small class="text-muted-color"
                                    >Type cannot be changed after
                                    creation.</small
                                >
                                }
                            </div>
                        </div>
                    </p-tabpanel>

                    <p-tabpanel value="currency">
                        <app-category-currency-form
                            [(value)]="currency"
                            [disabled]="saving()"
                        />
                    </p-tabpanel>

                    <p-tabpanel value="kyc">
                        <div class="flex flex-col gap-4">
                            <app-category-kyc-form
                                [(value)]="kyc"
                                [disabled]="saving()"
                            />
                            <div
                                class="flex flex-col gap-2 pt-3 border-t border-surface-200 dark:border-surface-700"
                            >
                                <div class="font-semibold">Sumsub</div>
                                <app-category-sumsub-form
                                    [(value)]="sumsub"
                                    [disabled]="saving()"
                                />
                            </div>
                        </div>
                    </p-tabpanel>

                    @if (type() === CategoryType.SkillBasedGames) {
                    <p-tabpanel value="withdraw">
                        <app-category-withdraw-form
                            [(value)]="withdraw"
                            [disabled]="saving()"
                        />
                    </p-tabpanel>
                    }

                    <p-tabpanel value="legal">
                        <div class="flex flex-col gap-2">
                            <small class="text-muted-color">
                                Category-default templates served on every
                                realm's legal pages; realms can override
                                individual documents in their own settings.
                            </small>
                            <app-category-legal-form
                                [(value)]="legal"
                                [disabled]="saving()"
                            />
                        </div>
                    </p-tabpanel>
                </p-tabpanels>
            </p-tabs>

            @if (generalError()) {
            <small class="text-red-500">{{ generalError() }}</small>
            }
        </div>
    </p-fluid>
    <ng-template #footer>
        <div class="flex justify-end gap-2">
            <p-button
                label="Cancel"
                severity="secondary"
                [disabled]="saving()"
                (onClick)="close()"
            />
            <p-button
                label="Save"
                icon="pi pi-check"
                [loading]="saving()"
                (onClick)="save()"
            />
        </div>
    </ng-template>
</p-dialog>
